import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Check24 from '../../icons/Check24';
import { currencyFormat } from '../../../utils/utils';
import { RJ, TITLE_FONT } from '../../rapport/rapportTokens';

// §5.2 — "Payé pour cette intervention" banner (paid state). Page-scoped brand
// charte (RJ tokens + AronVF), reused from the visit-report epic (#28). The
// global header/footer stay on LTSaeada — nothing here leaks to the theme.
const PaidBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  background: ${RJ.greenPastel};
  border-radius: ${RJ.radiusLg};
  padding: 14px 16px;
`;

const PaidIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 50%;
  background: ${RJ.green};
`;

const PaidLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5a7a52;
`;

const PaidAmount = styled.span`
  font-family: ${TITLE_FONT};
  font-weight: 900;
  font-size: 26px;
  color: ${RJ.text};
  white-space: nowrap;
`;

const isPaid = (course) =>
  !!(course && (course.stripe_payment_id || course.state === 'finished'));

const CurrentAppointmentBlock = ({ course }) => {
  const { t } = useTranslation();
  if (!course) return null;
  // Cancelled courses owe nothing and the CourseBanner above already
  // communicates the state — a green price banner would be misleading.
  if (course.state === 'cancelled') return null;

  // ticket #379 — the price banner shows the amount actually owed/paid, which
  // is only meaningful once the intervention is finished (or already paid).
  // Before that, course.prix is just the travel+insurance floor (e.g. 10 €),
  // so showing it would mislead the client — render nothing instead.
  if (!isPaid(course)) return null;

  const prix =
    typeof course.prix === 'number' ? course.prix : parseFloat(course.prix);
  const formatted = Number.isFinite(prix)
    ? currencyFormat(prix, course.currency)
    : null;

  return (
    <PaidBanner data-testid="current-appointment-block">
      <PaidIcon aria-hidden="true">
        <Check24 color="#314959" />
      </PaidIcon>
      <PaidLabel>{t('course.current.paid.label')}</PaidLabel>
      {formatted && (
        <PaidAmount data-testid="current-appointment-amount">
          {formatted}
        </PaidAmount>
      )}
    </PaidBanner>
  );
};

CurrentAppointmentBlock.propTypes = {
  course: PropTypes.shape({
    prix: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currency: PropTypes.string,
    state: PropTypes.string,
    stripe_payment_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
};

CurrentAppointmentBlock.defaultProps = {
  course: null,
};

export default CurrentAppointmentBlock;
